import './KYCForm.scss';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faMinus,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import Moment from 'moment';
import AuthService from '../../Services/AuthService';
import MonkeyTokenService from '../../Services/MonkeyTokenService';
import {
  ClassicForm,
  FormInput,
  FormTextArea,
  FormFileUpload,
  FormDropdown,
  FormDateInput,
} from '../../Components/XpektorForms/XpektorForms';
import RadioButton from '../../Components/RadioButton/RadioButton';
import XpektorButton from '../../Components/XpektorButton/XpektorButton';
import KYCService from '../../Services/KYCService';
import LoadingBar from '../../Components/LoadingBar/LoadingBar';
import Regex from '../../Components/Utilities/Regex';
import formatSwedishNumber from '../../Components/Utilities/SwedishPhoneNumberFormatter';
import { LuhnCheckSSN } from '../../Components/Utilities/HelperFunctions';

/**
 * @param {function} t
 * @param {string} key
 * @returns {string} translation of: page-kyc-form{key}
 */
const gT = (t, key) => t(`page-kyc-form${key}`);
const gsT = (t, key) => t(`shared${key}`);

/**
 * @param {function} t
 * @param {string} key
 * @returns {string} translation of: page-kyc-form.classic-form{key}
 */
const gTcf = (t, key) => gT(t, `.classic-form${key}`);

/**
 * @param {function} t
 * @param {string} key
 * @returns {string} translation of: page-kyc-form.classic-form.form-input{key}
 */
const gTfil = (t, key) => gTcf(t, `.form-input-label${key}`);

/**
 * @param {function} t
 * @param {string} key
 * @returns {string} translation of: page-kyc-form.classic-form.form-dropdown{key}
 */
const gTfdp = (t, key) => gTcf(t, `.form-dropdown${key}`);

/**
 * @param {function} t
 * @param {string} key
 * @returns {string} translation of: page-kyc-form.classic-form.form-file-upload-label{key}
 */
const gTfful = (t, key) => gTcf(t, `.form-file-upload-label${key}`);

/**
 * @param {function} t
 * @param {string} key
 * @returns {string} translation of: page-kyc-form.classic-form.form-text-area-label{key}
 */
const gTftal = (t, key) => gTcf(t, `.form-text-area-label${key}`);

/**
 * @param {function} t
 * @param {string} key
 * @returns {string} translation of: page-kyc-form.classic-form.form-text-area-label{key}
 */
const gTrb = (t, key) => gTcf(t, `.radio-btn${key}`);

export default function KYCForm() {
  const { t } = useTranslation('app');
  const { search } = useLocation();
  const stepRef = useRef();
  const maxSteps = 5;
  const state = new URLSearchParams(search).get('state');

  /* eslint-disable prettier/prettier */
  const [formStep, setFormStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [kycState, setKYCState] = useState({});
  const [pageTitle, setPageTitle] = useState('');
  const [riskBranch, setRiskbranch] = useState(false);
  const [moneyLaunderingFile, setMoneyLaunderingFile] = useState(null);
  const [formType, setFormType] = useState('0');
  const [companyName, setCompanyName] = useState('');
  const [orgNr, setOrgNr] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [bankAccountType, setBankAccountType] = useState(1);
  const [accountNumber, setAccountNumber] = useState('');
  const [accountProofFile, setAccountProofFile] = useState(null);
  const [contactPerson, setContactPerson] = useState('');
  const [phoneNrContactPerson, setPhoneNrContactPerson] = useState('');
  const [webAddress, setWebAddress] = useState('');
  const [eCommercePlatform, setECommercePlatform] = useState('');
  const [settlementEmail, setSettlementEmail] = useState('');
  const [incorporationDate, setIncorporationDate] = useState('');
  const [nrEmployees, setNrEmployees] = useState('');
  const [
    estimatedMonthlyRevenueInstitute,
    setEstimatedMonthlyRevenueInstitute,
  ] = useState('');
  const [averageAmountInvoice, setAverageAmountInvoice] = useState('');
  const [nrOfTransactionsInstitute, setNrOfTransactionsInstitute] =
    useState('');
  const [doesBusinessAbroad, setDoesBusinessAbroad] = useState(false);
  const [businessAbroadDescription, setBusinessAbroadDescription] =
    useState('');
  const [operationsOutsideEUEES, setOperationsOutsideEUESS] = useState(false);
  const [
    operationsOutsideEUEESDescription,
    setOperationsOutsideEUESSDescription,
  ] = useState('');
  const [coreBusinessDescription, setCoreBusinessDescription] = useState('');
  const [businessFinanceDescription, setBusinessFinanceDescription] =
    useState('');
  const [loanPurpose, setLoanPurpose] = useState('');
  const [foreignTransactionAmount, setForeignTransactionAmount] = useState(10);
  const [nrOfForeignTransactions, setNrOfForeignTransactions] = useState(0);
  const [currencyExchange, setCurrencyExchange] = useState(false);
  const [cryptoCurrencyExchange, setCryptoCurrencyExchange] = useState(false);
  const [handlingCash, setHandlingCash] = useState(false);
  // const [cashMonthly, setCashMonthly] = useState('');
  const [nrOfExtraRepayments, setNrOfExtraRepayments] = useState(0);
  const [companyHierarchyDescription, setCompanyHierarchyDescription] =
    useState('');
  const [companyHierarchyFile, setCompanyHierarchyFile] = useState(null);
  const [detailedBusinessDescription, setDetailedBusinessDescription] =
    useState('');
  const [detailedLoanPurpose, setDetailedLoanPurpose] = useState('');
  const [futurePlans, setFuturePlans] = useState('');
  const [estimatedRevenueThreeMonths, setEstimatedRevenueThreeMonths] =
    useState('');
  const [estimatedRevenueSixMonths, setEstimatedRevenueSixMonths] =
    useState('');
  const [estimatedRevenueTwelveMonths, setEstimatedRevenueTwelveMonths] =
    useState('');
  const [estimatedResultThreeMonths, setEstimatedResultThreeMonths] =
    useState('');
  const [estimatedResultSixMonths, setEstimatedResultSixMonths] = useState('');
  const [estimatedResultTwelveMonths, setEstimatedResultTwelveMonths] =
    useState('');
  const [revenueMonthly, setRevenueMothly] = useState('');
  const [sellingService, setSellingService] = useState(false);
  const [sellingProduct, setSellingProduct] = useState(false);
  const [otherRevenueSource, setOtherRevenueSource] = useState(false);
  const [serviceDescription, setServiceDescription] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [otherRevenueSourceDescription, setOtherRevenueSourceDescription] =
    useState('');
  const [nrOfWithdrawals, setNrOfWithdrawals] = useState('');
  const [otherSignatoriesExist, setOtherSignatoriesExist] = useState(false);
  const [otherSignatories, setOtherSignatories] = useState([]);
  const [beneficialOwnerPersons, setBeneficialOwnerPersons] = useState([]);
  const [beneficialOwnerCompanies, setBeneficialOwnerCompanies] = useState([]);
  const [otherInfluentialOwnersPresent, setOtherInfluentialOwnersPresent] =
    useState(false);
  const [otherInfluentialOwnerPersons, setOtherInfluentialOwnerPersons] =
    useState([]);
  const [otherInfluentialOwnerCompanies, setOtherInfluentialOwnerCompanies] =
    useState([]);
  const [count, setCount] = useState(0);
  const [possibleSubjects, setPossibleSubjects] = useState([]);
  const [submissionError, setSubmissionError] = useState('');
  const [errorArray, setErrorArray] = useState([]);
  const [boardMemberIndex, setBoardMemberIndex] = useState(0);
  const [boardMembers, setBoardMembers] = useState([
    {
      id: 'board-member-0',
      firstName: '',
      lastName: '',
      socSecurityNumber: '',
      isSignatory: true,
    },
  ]);
  /* eslint-enable prettier/prettier */

  const requiredErrorText = gsT(t, '.errors.empty-field');
  const emailErrorText = gsT(t, '.errors.invalid-email');
  const urlErrorText = gsT(t, '.errors.invalid-address');

  const orgNrErrorText = gT(t, '.error.orgnr');
  const zipCodeErrorText = gT(t, '.error.zip-code');
  const ssnErrorText = gT(t, '.error.ssn');
  const fileRequiredErrorText = gT(t, '.error.file-required');

  const addBoardMemberEntry = () => {
    const newMemberIndex = boardMemberIndex + 1;
    setBoardMemberIndex(newMemberIndex);

    setBoardMembers([
      ...boardMembers,
      {
        id: `board-member-${newMemberIndex}`,
        firstName: '',
        lastName: '',
        socSecurityNumber: '',
        isSignatory: true,
      },
    ]);
  };

  const addOtherSignatoryEntry = () => {
    setOtherSignatories([
      ...otherSignatories,
      {
        id: `other-signatory-${otherSignatories.length}`,
        firstName: '',
        lastName: '',
        socSecurityNumber: '',
      },
    ]);
  };

  const addBeneficialOwnerPersonEntry = () => {
    setBeneficialOwnerPersons([
      ...beneficialOwnerPersons,
      {
        id: `beneficial-owner-persons-${beneficialOwnerPersons.length}`,
        firstName: '',
        lastName: '',
        socSecurityNumber: '',
        shares: '',
        PEP: false,
        RCA: false,
      },
    ]);
  };

  const addBeneficialOwnerCompanyEntry = () => {
    setBeneficialOwnerCompanies([
      ...beneficialOwnerCompanies,
      {
        id: `beneficial-owner-companies-${beneficialOwnerCompanies.length}`,
        name: '',
        orgNumber: '',
        shares: '',
      },
    ]);
  };

  const addOtherInfluentialPerson = () => {
    setOtherInfluentialOwnerPersons([
      ...otherInfluentialOwnerPersons,
      {
        id: `other-influential-owner-person-${otherInfluentialOwnerPersons.length}`,
        firstName: '',
        lastName: '',
        socSecurityNumber: '',
        PEP: false,
        RCA: false,
        descriptionOfInfluenceInCompany: '',
      },
    ]);
  };

  const addOtherInfluentialCompany = () => {
    setOtherInfluentialOwnerCompanies([
      ...otherInfluentialOwnerCompanies,
      {
        id: `other-influential-owner-company-${otherInfluentialOwnerCompanies.length}`,
        name: '',
        orgNumber: '',
        descriptionOfInfluenceInCompany: '',
      },
    ]);
  };

  const autofillData = (data) => {
    setCompanyName(data.companyName ?? '');
    setOrgNr(data.orgNumber ?? '');
    setAddress(data.address ?? '');
    setCity(data.city ?? '');
    setZipCode(data.zipCode ?? '');
    setIncorporationDate(data.incorporationDate ?? '');
    setNrEmployees(data.nrEmployees ?? '');
    setRiskbranch(data.riskBranch ?? false);

    if (data.boardMembers.length > 0) {
      const newBoardMembers = [];
      for (let i = 0; i < data.boardMembers.length; i += 1) {
        newBoardMembers.push({
          id: `board-member-${i}`,
          firstName: data.boardMembers[i].firstName,
          lastName: data.boardMembers[i].lastName,
          socSecurityNumber: '',
          isSignatory: true,
        });
      }
      setBoardMembers(newBoardMembers);
    }
  };

  const refreshCheck = async () => {
    setCount(count + 1);

    const refreshTime = Moment(MonkeyTokenService.getExp()).subtract(
      20,
      'minutes'
    );
    if (Moment().isAfter(refreshTime)) {
      await AuthService.refreshMonkey()
        .then((response) => {
          MonkeyTokenService.setToken(response.data);
        })
        .catch(() => {});
    }
  };

  const submitKYCForm = async () => {
    const subjects = [];
    const subjectIdFiles = [];

    for (let i = 0; i < possibleSubjects.length; i += 1) {
      if (possibleSubjects[i].selected === true) {
        subjects.push({
          firstName: possibleSubjects[i].firstName,
          lastName: possibleSubjects[i].lastName,
          email: possibleSubjects[i].email,
          socSecurityNumber: possibleSubjects[i].socSecurityNumber,
        });
        subjectIdFiles.push(possibleSubjects[i].idFile);
      }
    }

    await KYCService.submitKYCForm(
      kycState.clientId,
      kycState.id,
      formType,
      companyName,
      orgNr,
      boardMembers,
      otherSignatories,
      address,
      city,
      zipCode,
      contactPerson,
      settlementEmail,
      phoneNrContactPerson,
      bankAccountType,
      accountNumber,
      webAddress,
      eCommercePlatform,
      incorporationDate,
      nrEmployees,
      estimatedMonthlyRevenueInstitute,
      averageAmountInvoice,
      nrOfTransactionsInstitute,
      doesBusinessAbroad,
      businessAbroadDescription,
      coreBusinessDescription,
      businessFinanceDescription,
      loanPurpose,
      doesBusinessAbroad ? foreignTransactionAmount : 0,
      doesBusinessAbroad ? nrOfForeignTransactions : 0,
      beneficialOwnerPersons,
      beneficialOwnerCompanies,
      companyHierarchyDescription,
      otherInfluentialOwnersPresent,
      otherInfluentialOwnerPersons,
      otherInfluentialOwnerCompanies,
      detailedLoanPurpose,
      detailedBusinessDescription,
      futurePlans,
      estimatedRevenueThreeMonths,
      estimatedRevenueSixMonths,
      estimatedRevenueTwelveMonths,
      estimatedResultThreeMonths,
      estimatedResultSixMonths,
      estimatedResultTwelveMonths,
      sellingService,
      sellingProduct,
      otherRevenueSource,
      serviceDescription,
      productDescription,
      otherRevenueSourceDescription,
      operationsOutsideEUEES,
      operationsOutsideEUEESDescription,
      currencyExchange,
      cryptoCurrencyExchange,
      revenueMonthly,
      handlingCash,
      0, // cash monthly, currently not used
      nrOfExtraRepayments,
      nrOfWithdrawals,
      companyHierarchyFile,
      accountProofFile,
      moneyLaunderingFile ?? '',
      subjects,
      subjectIdFiles
    )
      .then(() => setFormStep(maxSteps + 1))
      .catch(() => setSubmissionError(gT(t, '.error.submission.submit')))
      .finally(() => setLoading(false));
  };

  const getErrorById = (id) => {
    const errorText = errorArray.find((error) => error.id === id);
    return errorText !== undefined ? errorText.text : '';
  };

  const deleteErrorsIncluding = (id) => {
    setErrorArray(
      errorArray.filter((error) => {
        return !error.id.includes(id);
      })
    );
  };

  const deleteErrorById = (id) => {
    setErrorArray(
      errorArray.filter((error) => {
        return error.id !== id;
      })
    );
  };

  const setErrorById = (id, text) => {
    const existing = errorArray.find((error, index) => {
      if (error.id === id) {
        const newArray = [...errorArray];
        newArray[index] = { id, text };
        setErrorArray(newArray);

        return error;
      }
      return undefined;
    });
    if (existing === undefined) {
      setErrorArray([
        ...errorArray,
        {
          id,
          text,
        },
      ]);
    }
  };

  const validateRequired = (input, id) => {
    if (input.length === 0) {
      setErrorById(id, requiredErrorText);
      return false;
    }
    deleteErrorById(id);
    return true;
  };

  const validateZipCode = (input, id) => {
    if (!validateRequired(input, id)) return false;

    if (!Regex.testZipCode(input)) {
      setErrorById(id, zipCodeErrorText);
      return false;
    }
    return true;
  };

  const validateOrgNr = (input, id) => {
    if (!validateRequired(input, id)) return false;

    if (!Regex.testOrgNr(input)) {
      setErrorById(id, orgNrErrorText);
      return false;
    }
    return true;
  };

  const validateSSN = (input, id) => {
    if (!validateRequired(input, id)) return false;

    if (!Regex.testSSN(input) || !LuhnCheckSSN(input)) {
      setErrorById(id, ssnErrorText);
      return false;
    }
    return true;
  };

  const validateEmail = (input, id) => {
    if (!validateRequired(input, id)) return false;

    if (!Regex.testEmail(input)) {
      setErrorById(id, emailErrorText);
      return false;
    }
    return true;
  };

  const validateUrl = (input, id) => {
    if (input.length !== 0 && !Regex.testUrl(input)) {
      setErrorById(id, urlErrorText);
      return false;
    }

    deleteErrorById(id);
    return true;
  };

  const validateStep1 = () => {
    const newErrorArray = [];

    if (companyName.length === 0)
      newErrorArray.push({ id: 'companyName', text: requiredErrorText });

    if (orgNr.length === 0) {
      newErrorArray.push({ id: 'orgNr', text: requiredErrorText });
    } else if (!Regex.testOrgNr(orgNr)) {
      newErrorArray.push({ id: 'orgNr', text: orgNrErrorText });
    }

    if (address.length === 0)
      newErrorArray.push({ id: 'address', text: requiredErrorText });

    if (city.length === 0)
      newErrorArray.push({ id: 'city', text: requiredErrorText });

    if (zipCode.length === 0)
      newErrorArray.push({ id: 'zipCode', text: requiredErrorText });

    if (incorporationDate.length === 0)
      newErrorArray.push({ id: 'incorporationDate', text: requiredErrorText });

    if (nrEmployees.length === 0)
      newErrorArray.push({ id: 'nrEmployees', text: requiredErrorText });

    if (accountNumber.length === 0)
      newErrorArray.push({ id: 'accountNumber', text: requiredErrorText });

    if (accountProofFile === null)
      newErrorArray.push({
        id: 'accountProofFile',
        text: fileRequiredErrorText,
      });

    /* if (riskBranch && moneyLaunderingFile === null)
      newErrorArray.push({
        id: 'moneyLaunderingFile',
        text: fileRequiredErrorText,
      }); */

    if (contactPerson.length === 0)
      newErrorArray.push({ id: 'contactPerson', text: requiredErrorText });

    if (phoneNrContactPerson.length === 0)
      newErrorArray.push({
        id: 'phoneNrContactPerson',
        text: requiredErrorText,
      });

    if (webAddress.length > 0 && !Regex.testUrl(webAddress))
      newErrorArray.push({
        id: 'webAddress',
        text: urlErrorText,
      });

    if (eCommercePlatform.length > 0 && !Regex.testUrl(eCommercePlatform))
      newErrorArray.push({
        id: 'eCommercePlatform',
        text: urlErrorText,
      });

    if (formType === 2 && settlementEmail.length === 0)
      newErrorArray.push({ id: 'settlementEmail', text: requiredErrorText });

    for (let i = 0; i < boardMembers.length; i += 1) {
      if (boardMembers[i].firstName.length === 0) {
        newErrorArray.push({
          id: `${boardMembers[i].id}-first-name`,
          text: requiredErrorText,
        });
      }
      if (boardMembers[i].lastName.length === 0) {
        newErrorArray.push({
          id: `${boardMembers[i].id}-last-name`,
          text: requiredErrorText,
        });
      }
      if (boardMembers[i].socSecurityNumber.length === 0) {
        newErrorArray.push({
          id: `${boardMembers[i].id}-ssn`,
          text: requiredErrorText,
        });
      } else if (
        !Regex.testSSN(boardMembers[i].socSecurityNumber) ||
        !LuhnCheckSSN(boardMembers[i].socSecurityNumber)
      ) {
        newErrorArray.push({
          id: `${boardMembers[i].id}-ssn`,
          text: ssnErrorText,
        });
      }
    }

    if (otherSignatoriesExist) {
      for (let i = 0; i < otherSignatories.length; i += 1) {
        if (otherSignatories[i].firstName.length === 0) {
          newErrorArray.push({
            id: `${otherSignatories[i].id}-first-name`,
            text: requiredErrorText,
          });
        }
        if (otherSignatories[i].lastName.length === 0) {
          newErrorArray.push({
            id: `${otherSignatories[i].id}-last-name`,
            text: requiredErrorText,
          });
        }
        if (otherSignatories[i].socSecurityNumber.length === 0) {
          newErrorArray.push({
            id: `${otherSignatories[i].id}-ssn`,
            text: requiredErrorText,
          });
        } else if (
          !Regex.testSSN(otherSignatories[i].socSecurityNumber) ||
          !LuhnCheckSSN(otherSignatories[i].socSecurityNumber)
        ) {
          newErrorArray.push({
            id: `${otherSignatories[i].id}-ssn`,
            text: ssnErrorText,
          });
        }
      }
    }

    setErrorArray(newErrorArray);
    if (newErrorArray.length > 0) {
      return false;
    }

    return true;
  };

  const validateStep2 = () => {
    const newErrorArray = [];

    if (companyHierarchyDescription.length === 0) {
      newErrorArray.push({
        id: 'companyHierarchyDescription',
        text: requiredErrorText,
      });
    }

    if (coreBusinessDescription.length === 0) {
      newErrorArray.push({
        id: 'coreBusinessDescription',
        text: requiredErrorText,
      });
    }

    if (detailedBusinessDescription.length === 0) {
      newErrorArray.push({
        id: 'detailedBusinessDescription',
        text: requiredErrorText,
      });
    }

    if (businessFinanceDescription.length === 0) {
      newErrorArray.push({
        id: 'businessFinanceDescription',
        text: requiredErrorText,
      });
    }

    if (doesBusinessAbroad && businessAbroadDescription.length === 0) {
      newErrorArray.push({
        id: 'businessAbroadDescription',
        text: requiredErrorText,
      });
    }

    if (
      operationsOutsideEUEES &&
      operationsOutsideEUEESDescription.length === 0
    ) {
      newErrorArray.push({
        id: 'operationsOutsideEUEESDescription',
        text: requiredErrorText,
      });
    }

    for (let i = 0; i < beneficialOwnerPersons.length; i += 1) {
      if (beneficialOwnerPersons[i].firstName.length === 0) {
        newErrorArray.push({
          id: `${beneficialOwnerPersons[i].id}-first-name`,
          text: requiredErrorText,
        });
      }
      if (beneficialOwnerPersons[i].lastName.length === 0) {
        newErrorArray.push({
          id: `${beneficialOwnerPersons[i].id}-last-name`,
          text: requiredErrorText,
        });
      }
      if (beneficialOwnerPersons[i].socSecurityNumber.length === 0) {
        newErrorArray.push({
          id: `${beneficialOwnerPersons[i].id}-ssn`,
          text: requiredErrorText,
        });
      } else if (
        !Regex.testSSN(beneficialOwnerPersons[i].socSecurityNumber) ||
        !LuhnCheckSSN(beneficialOwnerPersons[i].socSecurityNumber)
      ) {
        newErrorArray.push({
          id: `${beneficialOwnerPersons[i].id}-ssn`,
          text: ssnErrorText,
        });
      }
      if (beneficialOwnerPersons[i].shares.length === 0) {
        newErrorArray.push({
          id: `${beneficialOwnerPersons[i].id}-shares`,
          text: requiredErrorText,
        });
      }
    }

    for (let i = 0; i < beneficialOwnerCompanies.length; i += 1) {
      if (beneficialOwnerCompanies[i].name.length === 0) {
        newErrorArray.push({
          id: `${beneficialOwnerCompanies[i].id}-name`,
          text: requiredErrorText,
        });
      }
      if (beneficialOwnerCompanies[i].orgNumber.length === 0) {
        newErrorArray.push({
          id: `${beneficialOwnerCompanies[i].id}-orgNumber`,
          text: requiredErrorText,
        });
      } else if (!Regex.testOrgNr(beneficialOwnerCompanies[i].orgNumber)) {
        newErrorArray.push({
          id: `${beneficialOwnerCompanies[i].id}-orgNumber`,
          text: ssnErrorText,
        });
      }
      if (beneficialOwnerCompanies[i].shares.length === 0) {
        newErrorArray.push({
          id: `${beneficialOwnerCompanies[i].id}-shares`,
          text: requiredErrorText,
        });
      }
    }

    if (otherInfluentialOwnersPresent) {
      for (let i = 0; i < otherInfluentialOwnerPersons.length; i += 1) {
        if (otherInfluentialOwnerPersons[i].firstName.length === 0) {
          newErrorArray.push({
            id: `${otherInfluentialOwnerPersons[i].id}-first-name`,
            text: requiredErrorText,
          });
        }
        if (otherInfluentialOwnerPersons[i].lastName.length === 0) {
          newErrorArray.push({
            id: `${otherInfluentialOwnerPersons[i].id}-last-name`,
            text: requiredErrorText,
          });
        }
        if (otherInfluentialOwnerPersons[i].socSecurityNumber.length === 0) {
          newErrorArray.push({
            id: `${otherInfluentialOwnerPersons[i].id}-ssn`,
            text: requiredErrorText,
          });
        } else if (
          !Regex.testSSN(otherInfluentialOwnerPersons[i].socSecurityNumber) ||
          !LuhnCheckSSN(otherInfluentialOwnerPersons[i].socSecurityNumber)
        ) {
          newErrorArray.push({
            id: `${otherInfluentialOwnerPersons[i].id}-ssn`,
            text: ssnErrorText,
          });
        }
        if (
          otherInfluentialOwnerPersons[i].descriptionOfInfluenceInCompany
            .length === 0
        ) {
          newErrorArray.push({
            id: `${otherInfluentialOwnerPersons[i].id}-description`,
            text: requiredErrorText,
          });
        }
      }

      for (let i = 0; i < otherInfluentialOwnerCompanies.length; i += 1) {
        if (otherInfluentialOwnerCompanies[i].name.length === 0) {
          newErrorArray.push({
            id: `${otherInfluentialOwnerCompanies[i].id}-name`,
            text: requiredErrorText,
          });
        }
        if (otherInfluentialOwnerCompanies[i].orgNumber.length === 0) {
          newErrorArray.push({
            id: `${otherInfluentialOwnerCompanies[i].id}-orgNumber`,
            text: requiredErrorText,
          });
        } else if (
          !Regex.testOrgNr(otherInfluentialOwnerCompanies[i].orgNumber)
        ) {
          newErrorArray.push({
            id: `${otherInfluentialOwnerCompanies[i].id}-orgNumber`,
            text: ssnErrorText,
          });
        }
        if (
          otherInfluentialOwnerCompanies[i].descriptionOfInfluenceInCompany
            .length === 0
        ) {
          newErrorArray.push({
            id: `${otherInfluentialOwnerCompanies[i].id}-description`,
            text: requiredErrorText,
          });
        }
      }
    }

    setErrorArray(newErrorArray);
    if (newErrorArray.length > 0) {
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    const newErrorArray = [];

    if (loanPurpose.length === 0) {
      newErrorArray.push({
        id: 'loanPurpose',
        text: requiredErrorText,
      });
    }

    if (formType === '0' && detailedLoanPurpose.length === 0) {
      newErrorArray.push({
        id: 'detailedLoanPurpose',
        text: requiredErrorText,
      });
    }

    if (futurePlans.length === 0) {
      newErrorArray.push({
        id: 'futurePlans',
        text: requiredErrorText,
      });
    }

    if (estimatedRevenueThreeMonths.length === 0) {
      newErrorArray.push({
        id: 'estimatedRevenueThreeMonths',
        text: requiredErrorText,
      });
    }

    if (estimatedRevenueSixMonths.length === 0) {
      newErrorArray.push({
        id: 'estimatedRevenueSixMonths',
        text: requiredErrorText,
      });
    }

    if (estimatedRevenueTwelveMonths.length === 0) {
      newErrorArray.push({
        id: 'estimatedRevenueTwelveMonths',
        text: requiredErrorText,
      });
    }

    if (estimatedResultThreeMonths.length === 0) {
      newErrorArray.push({
        id: 'estimatedResultThreeMonths',
        text: requiredErrorText,
      });
    }

    if (estimatedResultSixMonths.length === 0) {
      newErrorArray.push({
        id: 'estimatedResultSixMonths',
        text: requiredErrorText,
      });
    }

    if (estimatedResultTwelveMonths.length === 0) {
      newErrorArray.push({
        id: 'estimatedResultTwelveMonths',
        text: requiredErrorText,
      });
    }

    if (revenueMonthly.length === 0) {
      newErrorArray.push({
        id: 'revenueMonthly',
        text: requiredErrorText,
      });
    }

    if (sellingProduct && productDescription.length === 0) {
      newErrorArray.push({
        id: 'productDescription',
        text: requiredErrorText,
      });
    }

    if (sellingService && serviceDescription.length === 0) {
      newErrorArray.push({
        id: 'serviceDescription',
        text: requiredErrorText,
      });
    }

    if (otherRevenueSource && otherRevenueSourceDescription.length === 0) {
      newErrorArray.push({
        id: 'otherRevenueSourceDescription',
        text: requiredErrorText,
      });
    }

    if (formType === '2') {
      if (estimatedMonthlyRevenueInstitute.length === 0) {
        newErrorArray.push({
          id: 'estimatedMonthlyRevenueInstitute',
          text: requiredErrorText,
        });
      }

      if (nrOfTransactionsInstitute.length === 0) {
        newErrorArray.push({
          id: 'nrOfTransactionsInstitute',
          text: requiredErrorText,
        });
      }

      if (averageAmountInvoice.length === 0) {
        newErrorArray.push({
          id: 'averageAmountInvoice',
          text: requiredErrorText,
        });
      }
    }

    setErrorArray(newErrorArray);
    if (newErrorArray.length > 0) {
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    const newErrorArray = [];

    /* if (handlingCash && cashMonthly.length === 0) {
      newErrorArray.push({
        id: 'cashMonthly',
        text: requiredErrorText,
      });
    } */

    if (nrOfWithdrawals.length === 0) {
      newErrorArray.push({
        id: 'nrOfWithdrawals',
        text: requiredErrorText,
      });
    }

    setErrorArray(newErrorArray);
    if (newErrorArray.length > 0) {
      return false;
    }
    return true;
  };

  const validateStep5 = () => {
    const newErrorArray = [];

    const selectedSubjects = possibleSubjects.filter(
      (subject) => subject.selected === true
    );
    if (selectedSubjects.length === 0) {
      return false;
    }

    for (let i = 0; i < selectedSubjects.length; i += 1) {
      if (selectedSubjects[i].idFile === null) {
        newErrorArray.push({
          id: `${selectedSubjects[i].id}-file`,
          text: fileRequiredErrorText,
        });
      }
      if (selectedSubjects[i].email.length === 0) {
        newErrorArray.push({
          id: `${selectedSubjects[i].id}-email`,
          text: requiredErrorText,
        });
      } else if (!Regex.testEmail(selectedSubjects[i].email)) {
        newErrorArray.push({
          id: `${selectedSubjects[i].id}-email`,
          text: emailErrorText,
        });
      }
    }

    setErrorArray(newErrorArray);
    if (newErrorArray.length > 0) {
      return false;
    }
    return true;
  };

  const scrollToTop = () => {
    const element = stepRef.current;
    const offset = 20; // Adjust the offset value as needed
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  const goPrevious = () => {
    if (formStep > 1) {
      setFormStep(formStep - 1);
      scrollToTop();
    }
  };

  const onNext = () => {
    setFormStep(formStep + 1);
    setSubmissionError('');
    scrollToTop();
  };

  const goNext = () => {
    setErrorArray([]);
    if (formStep === 1) {
      if (validateStep1()) {
        onNext();
      } else {
        setSubmissionError(gT(t, '.error.submission.error-in-form'));
      }
    } else if (formStep === 2) {
      if (validateStep2()) {
        onNext();
      } else {
        setSubmissionError(gT(t, '.error.submission.error-in-form'));
      }
    } else if (formStep === 3) {
      if (validateStep3()) {
        onNext();
      } else {
        setSubmissionError(gT(t, '.error.submission.error-in-form'));
      }
    } else if (formStep === 4) {
      if (validateStep4()) {
        onNext();
      } else {
        setSubmissionError(gT(t, '.error.submission.error-in-form'));
      }
    } else if (formStep === maxSteps) {
      if (validateStep5()) {
        setLoading(true);
        setSubmissionError('');
        submitKYCForm();
      } else {
        setSubmissionError(gT(t, '.error.submission.error-in-form'));
      }
    }
  };

  useEffect(() => {
    const decodeState = async () => {
      if (state) {
        const response = await AuthService.decodeKYC(state);
        if (response != null) {
          setKYCState(response);
          const newPageTitle = `${response.customerName
            .split(' ')
            .filter((word) => word !== 'AB')
            .join(' ')} ${gT(t, '.know-your-customer')}`;
          document.title = newPageTitle;
          setPageTitle(newPageTitle);
          if (response.autofillData !== null) {
            autofillData(response.autofillData);
          }
        } else {
          setKYCState(null);
          document.title = gsT(t, '.errors.invalid-link');
        }
        setLoading(false);
      }
    };
    decodeState();
    // eslint-disable-next-line
  }, [state]);

  useEffect(() => {
    const timer = setTimeout(() => refreshCheck(), 60000); // Every minute
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [count]);

  useEffect(() => {
    const newPossibleSubjects = [];
    for (let i = 0; i < boardMembers.length; i += 1) {
      if (boardMembers[i].isSignatory === true) {
        const existingPossibleSubject = newPossibleSubjects.find(
          (existing) =>
            existing.socSecurityNumber === boardMembers[i].socSecurityNumber
        );
        if (existingPossibleSubject === undefined) {
          newPossibleSubjects.push({
            id: `possible-subject-${newPossibleSubjects.length}`,
            firstName: boardMembers[i].firstName,
            lastName: boardMembers[i].lastName,
            socSecurityNumber: boardMembers[i].socSecurityNumber,
            email: '',
            idFile: null,
            selected: false,
          });
        }
      }
    }
    for (let i = 0; i < otherSignatories.length; i += 1) {
      const existingPossibleSubject = newPossibleSubjects.find(
        (existing) =>
          existing.socSecurityNumber === otherSignatories[i].socSecurityNumber
      );
      if (existingPossibleSubject === undefined) {
        newPossibleSubjects.push({
          id: `possible-subject-${newPossibleSubjects.length}`,
          firstName: otherSignatories[i].firstName,
          lastName: otherSignatories[i].lastName,
          socSecurityNumber: otherSignatories[i].socSecurityNumber,
          email: '',
          idFile: null,
          selected: false,
        });
      }
    }
    setPossibleSubjects(newPossibleSubjects);
  }, [boardMembers, otherSignatories]);

  return (
    <div className="kyc-form-wrapper">
      <div className="kyc-modal">
        {loading ? (
          <LoadingBar status={gT(t, '.loading-bar')} active />
        ) : kycState === null ? (
          <p className="unauthorized-text">{gT(t, '.error.invalid-link')}</p>
        ) : (
          <>
            <div className="kyc-header">
              <div className="title-row">
                <h1 className="kyc-title">{pageTitle}</h1>
                {kycState.customerLogo && (
                  <img
                    className="logo"
                    src={kycState.customerLogo}
                    alt="Logo"
                  />
                )}
              </div>
              <p className="normal-text">
                {gT(t, '.normal-text.financial-terror')}
                <br />
                <br />
                {gT(t, '.normal-text.prefix-questions-identification')}
                &nbsp;{kycState.supportEmail ?? 'info@xpektor.se'}
                <br />
                {gT(t, '.normal-text.prefix-help-guidance')}
                &nbsp;
                {kycState.supportPhoneNumber
                  ? formatSwedishNumber(kycState.supportPhoneNumber)
                  : '076-942 47 44'}
                &nbsp;{gT(t, '.normal-text.suffix-help-guidance')}
                &nbsp;{gT(t, '.normal-text.verification-bankid')}
              </p>
              {/* eslint-disable-next-line prettier/prettier */}
              {formStep < maxSteps + 1 && (
                <div className="step-display" ref={stepRef}>
                  <p>{`${gT(t, '.prefix-step')} ${formStep}/${maxSteps}`}</p>
                  <div className="step-lights">
                    <div
                      className={`step-light ${
                        formStep === 1 ? 'active-light' : ''
                      }`}
                    />
                    <div
                      className={`step-light ${
                        formStep === 2 ? 'active-light' : ''
                      }`}
                    />
                    <div
                      className={`step-light ${
                        formStep === 3 ? 'active-light' : ''
                      }`}
                    />
                    <div
                      className={`step-light ${
                        formStep === 4 ? 'active-light' : ''
                      }`}
                    />
                    <div
                      className={`step-light ${
                        formStep === 5 ? 'active-light' : ''
                      }`}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="kyc-content">
              {formStep === 1 ? (
                <ClassicForm>
                  <div className="form-section">
                    <FormDropdown
                      label={gTfdp(t, '.label-form-type')}
                      onChange={(e) => setFormType(e.target.value)}
                      defaultValue={0}
                    >
                      <option value={0}>{gTfdp(t, '.form-type.0')}</option>
                      <option value={1}>{gTfdp(t, '.form-type.1')}</option>
                      <option value={2}>{gTfdp(t, '.form-type.2')}</option>
                      <option value={3}>{gTfdp(t, '.form-type.3')}</option>
                      <option value={4}>{gTfdp(t, '.form-type.4')}</option>
                    </FormDropdown>
                  </div>
                  <div className="form-section">
                    <div className="form-grid">
                      <FormInput
                        label={`${gTfil(t, '.company-name')} *`}
                        value={companyName}
                        onChange={(e) => {
                          setCompanyName(e.target.value);
                          validateRequired(e.target.value, 'companyName');
                        }}
                        infoText={getErrorById('companyName')}
                        maxLength={100}
                      />
                      <FormInput
                        label={`${gTfil(t, '.orgnr')} *`}
                        value={orgNr}
                        onChange={(e) => {
                          setOrgNr(e.target.value);
                          validateOrgNr(e.target.value, 'orgNr');
                        }}
                        infoText={getErrorById('orgNr')}
                        maxLength={100}
                      />
                      <FormInput
                        label={`${gTfil(t, '.address')} *`}
                        value={address}
                        onChange={(e) => {
                          setAddress(e.target.value);
                          validateRequired(e.target.value, 'address');
                        }}
                        infoText={getErrorById('address')}
                        maxLength={100}
                      />
                      <FormInput
                        label={`${gTfil(t, '.city')} *`}
                        value={city}
                        onChange={(e) => {
                          setCity(e.target.value);
                          validateRequired(e.target.value, 'city');
                        }}
                        infoText={getErrorById('city')}
                        maxLength={100}
                      />
                      <FormInput
                        label={`${gTfil(t, '.zip-code')} *`}
                        value={zipCode}
                        onChange={(e) => {
                          setZipCode(e.target.value);
                          validateZipCode(e.target.value, 'zipCode');
                        }}
                        infoText={getErrorById('zipCode')}
                        maxLength={100}
                      />
                      <FormDateInput
                        label={gTfil(t, '.incorporation-date')}
                        value={incorporationDate}
                        onChange={(e) => {
                          setIncorporationDate(e.target.value);
                          validateRequired(e.target.value, 'incorporationDate');
                        }}
                        infoText={getErrorById('incorporationDate')}
                      />
                      <FormInput
                        label={`${gTfil(t, '.nr-employees')} *`}
                        value={nrEmployees}
                        regex={Regex.testIntegerInput}
                        onChange={(e) => {
                          setNrEmployees(e.target.value);
                          validateRequired(e.target.value, 'nrEmployees');
                        }}
                        infoText={getErrorById('nrEmployees')}
                        maxLength={10}
                      />
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="form-column">
                      <div className="form-row">
                        <FormInput
                          label={`${gTfil(t, '.account-number')} *`}
                          value={accountNumber}
                          regex={Regex.testAccountNumberInput}
                          onChange={(e) => {
                            setAccountNumber(e.target.value);
                            validateRequired(e.target.value, 'accountNumber');
                          }}
                          infoText={getErrorById('accountNumber')}
                          maxLength={100}
                        />
                        <div className="form-row">
                          <FormDropdown
                            label={gTfdp(t, '.label-bank-account-type')}
                            onChange={(e) => setBankAccountType(e.target.value)}
                            defaultValue={1}
                          >
                            <option value={1}>
                              {gTfdp(t, '.bank-account-type.0')}
                            </option>
                            <option value={2}>
                              {gTfdp(t, '.bank-account-type.1')}
                            </option>
                            <option value={3}>
                              {gTfdp(t, '.bank-account-type.2')}
                            </option>
                            <option value={4}>
                              {gTfdp(t, '.bank-account-type.3')}
                            </option>
                          </FormDropdown>
                          <div />
                        </div>
                      </div>
                      <div className="form-row">
                        <FormFileUpload
                          label={`${gTfful(t, '.account-proof-file')} *`}
                          onChange={(e) => {
                            if (e.target.files.length > 0) {
                              setAccountProofFile(e.target.files[0]);
                              deleteErrorById('accountProofFile');
                            } else {
                              setAccountProofFile(null);
                              setErrorById('accountProofFile');
                            }
                          }}
                          infoText={getErrorById('accountProofFile')}
                          value={accountProofFile}
                          t={t}
                        />
                      </div>
                    </div>
                  </div>
                  {riskBranch && (
                    <div className="form-section">
                      <div className="form-column">
                        <p className="small-title">
                          {gT(t, '.small-title.risk-branch')}
                        </p>
                        <p className="normal-text">
                          {gT(t, '.normal-text.risk-branch')}
                        </p>
                        <div className="form-row">
                          <FormFileUpload
                            label={gTfful(t, '.money-laundering-file')}
                            onChange={(e) => {
                              if (e.target.files.length > 0) {
                                setMoneyLaunderingFile(e.target.files[0]);
                                // deleteErrorById('moneyLaunderingFile');
                              } else {
                                setMoneyLaunderingFile(null);
                                // setErrorById('moneyLaunderingFile');
                              }
                            }}
                            infoText={getErrorById('moneyLaunderingFile')}
                            value={moneyLaunderingFile}
                            t={t}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="form-section">
                    <div className="form-column">
                      <p className="small-title">
                        {gT(t, '.small-title.board-members')}
                      </p>
                      {boardMembers.map((boardMember, index) => (
                        <div className="list-entry" key={boardMember.id}>
                          <div className="list-row">
                            <div className="tight-form-row">
                              <FormInput
                                label={`${gTfil(t, '.first-name')} *`}
                                value={boardMember.firstName}
                                onChange={(e) => {
                                  const newArray = [...boardMembers];
                                  newArray[index].firstName = e.target.value;
                                  setBoardMembers(newArray);
                                  validateRequired(
                                    e.target.value,
                                    `${boardMember.id}-first-name`
                                  );
                                }}
                                infoText={getErrorById(
                                  `${boardMember.id}-first-name`
                                )}
                                maxLength={100}
                              />
                              <FormInput
                                label={`${gTfil(t, '.last-name')} *`}
                                value={boardMember.lastName}
                                onChange={(e) => {
                                  const newArray = [...boardMembers];
                                  newArray[index].lastName = e.target.value;
                                  setBoardMembers(newArray);
                                  validateRequired(
                                    e.target.value,
                                    `${boardMember.id}-last-name`
                                  );
                                }}
                                infoText={getErrorById(
                                  `${boardMember.id}-last-name`
                                )}
                                maxLength={100}
                              />
                              <FormInput
                                label={`${gTfil(t, '.soc-security-number')} *`}
                                value={boardMember.socSecurityNumber}
                                onChange={(e) => {
                                  const newArray = [...boardMembers];
                                  newArray[index].socSecurityNumber =
                                    e.target.value;
                                  setBoardMembers(newArray);
                                  validateSSN(
                                    e.target.value,
                                    `${boardMember.id}-ssn`
                                  );
                                }}
                                infoText={getErrorById(`${boardMember.id}-ssn`)}
                                maxLength={20}
                              />
                              <FormDropdown
                                label={gTfdp(t, '.label-is-signatory')}
                                onChange={(e) => {
                                  const newArray = [...boardMembers];
                                  newArray[index].isSignatory =
                                    e.target.value === gT(t, '.yes');
                                  setBoardMembers(newArray);
                                }}
                                defaultValue={gT(t, '.yes')}
                              >
                                <option>{gT(t, '.yes')}</option>
                                <option>{gT(t, '.no')}</option>
                              </FormDropdown>
                            </div>
                            <button
                              type="button"
                              className="modify-button remove-button"
                              disabled={boardMembers.length === 1}
                              onClick={() => {
                                deleteErrorsIncluding(boardMember.id);
                                setBoardMembers(
                                  boardMembers.filter((filterBoardMember) => {
                                    return filterBoardMember !== boardMember;
                                  })
                                );
                              }}
                            >
                              <FontAwesomeIcon
                                className="icon"
                                icon={faMinus}
                              />
                              <p>{gTcf(t, '.btn.remove')}</p>
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        className="modify-button"
                        type="button"
                        onClick={() => addBoardMemberEntry()}
                      >
                        <FontAwesomeIcon className="icon" icon={faPlus} />
                        <p>{gTcf(t, '.btn.add-person')}</p>
                      </button>
                    </div>
                    <div className="form-column">
                      <div className="radio-select">
                        <p className="small-title">
                          {gT(t, '.small-title.other-signatories-exist')}
                        </p>
                        <div className="choice-row">
                          <RadioButton
                            text={gT(t, '.yes')}
                            active={otherSignatoriesExist}
                            onClick={() => setOtherSignatoriesExist(true)}
                          />
                          <RadioButton
                            text={gT(t, '.no')}
                            active={!otherSignatoriesExist}
                            onClick={() => {
                              setOtherSignatoriesExist(false);
                              setOtherSignatories([]);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    {otherSignatoriesExist && (
                      <div className="form-column">
                        <p className="small-title">
                          {gT(t, '.small-title.other-signatories')}
                        </p>
                        {otherSignatories.map((signatory, index) => (
                          <div className="list-entry" key={signatory.id}>
                            <div className="list-row">
                              <div className="tight-form-row">
                                <FormInput
                                  label={`${gTfil(t, '.first-name')} *`}
                                  value={signatory.firstName}
                                  onChange={(e) => {
                                    const newArray = [...otherSignatories];
                                    newArray[index].firstName = e.target.value;
                                    setOtherSignatories(newArray);
                                    validateRequired(
                                      e.target.value,
                                      `${signatory.id}-first-name`
                                    );
                                  }}
                                  infoText={getErrorById(
                                    `${signatory.id}-first-name`
                                  )}
                                  maxLength={100}
                                />
                                <FormInput
                                  label={`${gTfil(t, '.last-name')} *`}
                                  value={signatory.lastName}
                                  onChange={(e) => {
                                    const newArray = [...otherSignatories];
                                    newArray[index].lastName = e.target.value;
                                    setOtherSignatories(newArray);
                                    validateRequired(
                                      e.target.value,
                                      `${signatory.id}-last-name`
                                    );
                                  }}
                                  infoText={getErrorById(
                                    `${signatory.id}-last-name`
                                  )}
                                  maxLength={100}
                                />
                                <FormInput
                                  label={`${gTfil(
                                    t,
                                    '.soc-security-number'
                                  )} *`}
                                  value={signatory.socSecurityNumber}
                                  onChange={(e) => {
                                    const newArray = [...otherSignatories];
                                    newArray[index].socSecurityNumber =
                                      e.target.value;
                                    setOtherSignatories(newArray);
                                    validateSSN(
                                      e.target.value,
                                      `${signatory.id}-ssn`
                                    );
                                  }}
                                  infoText={getErrorById(`${signatory.id}-ssn`)}
                                  maxLength={20}
                                />
                              </div>
                              <button
                                type="button"
                                className="modify-button remove-button"
                                onClick={() => {
                                  deleteErrorsIncluding(signatory.id);
                                  setOtherSignatories(
                                    otherSignatories.filter(
                                      (filterSignatory) => {
                                        return filterSignatory !== signatory;
                                      }
                                    )
                                  );
                                }}
                              >
                                <FontAwesomeIcon
                                  className="icon"
                                  icon={faMinus}
                                />
                                <p>{gTcf(t, '.btn.remove')}</p>
                              </button>
                            </div>
                          </div>
                        ))}
                        <button
                          className="modify-button"
                          type="button"
                          onClick={() => addOtherSignatoryEntry()}
                        >
                          <FontAwesomeIcon className="icon" icon={faPlus} />
                          <p>{gTcf(t, '.btn.add-person')}</p>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="form-section">
                    <div className="form-grid">
                      <FormInput
                        label={`${gTfil(t, '.contact-person')} *`}
                        value={contactPerson}
                        onChange={(e) => {
                          setContactPerson(e.target.value);
                          validateRequired(e.target.value, 'contactPerson');
                        }}
                        infoText={getErrorById('contactPerson')}
                        maxLength={100}
                      />
                      <FormInput
                        label={`${gTfil(t, '.phone-nr-contact-person')} *`}
                        value={phoneNrContactPerson}
                        regex={Regex.testPhoneNrInput}
                        onChange={(e) => {
                          setPhoneNrContactPerson(e.target.value);
                          validateRequired(
                            e.target.value,
                            'phoneNrContactPerson'
                          );
                        }}
                        infoText={getErrorById('phoneNrContactPerson')}
                        maxLength={100}
                      />
                      <FormInput
                        label={gTfil(t, '.web-address')}
                        value={webAddress}
                        onChange={(e) => {
                          setWebAddress(e.target.value);
                          validateUrl(e.target.value, 'webAddress');
                        }}
                        infoText={getErrorById('webAddress')}
                        maxLength={100}
                      />
                      <FormInput
                        label={gTfil(t, '.e-commerce-platform')}
                        value={eCommercePlatform}
                        onChange={(e) => {
                          setECommercePlatform(e.target.value);
                          validateUrl(e.target.value, 'eCommercePlatform');
                        }}
                        infoText={getErrorById('eCommercePlatform')}
                        maxLength={100}
                      />
                      {formType === '2' && (
                        <FormInput
                          label={`${gTfil(t, '.settlement-email')} *`}
                          value={settlementEmail}
                          onChange={(e) => {
                            setSettlementEmail(e.target.value);
                            validateEmail(e.target.value, 'settlementEmail');
                          }}
                          infoText={getErrorById('settlementEmail')}
                          maxLength={100}
                        />
                      )}
                    </div>
                  </div>
                </ClassicForm>
              ) : formStep === 2 ? (
                <ClassicForm>
                  <div className="form-section">
                    <div className="form-column">
                      <p className="medium-title">
                        {gT(t, '.medium-title.beneficial-owner')}
                      </p>
                      <p className="normal-text">
                        <Trans
                          i18nKey={gT(t, '.normal-text.beneficial-owner')}
                        />
                      </p>
                      <p className="small-title">
                        {gT(t, '.small-title.people')}
                      </p>
                      {beneficialOwnerPersons.map((beneficialOwner, index) => (
                        <div className="list-entry" key={beneficialOwner.id}>
                          <div className="list-row">
                            <div className="tight-form-row">
                              <FormInput
                                label={`${gTfil(t, '.first-name')} *`}
                                value={beneficialOwner.firstName}
                                onChange={(e) => {
                                  const newArray = [...beneficialOwnerPersons];
                                  newArray[index].firstName = e.target.value;
                                  setBeneficialOwnerPersons(newArray);
                                  validateRequired(
                                    e.target.value,
                                    `${beneficialOwner.id}-first-name`
                                  );
                                }}
                                infoText={getErrorById(
                                  `${beneficialOwner.id}-first-name`
                                )}
                                maxLength={100}
                              />
                              <FormInput
                                label={`${gTfil(t, '.last-name')} *`}
                                value={beneficialOwner.lastName}
                                onChange={(e) => {
                                  const newArray = [...beneficialOwnerPersons];
                                  newArray[index].lastName = e.target.value;
                                  setBeneficialOwnerPersons(newArray);
                                  validateRequired(
                                    e.target.value,
                                    `${beneficialOwner.id}-last-name`
                                  );
                                }}
                                infoText={getErrorById(
                                  `${beneficialOwner.id}-last-name`
                                )}
                                maxLength={100}
                              />
                              <FormInput
                                label={`${gTfil(t, '.soc-security-number')} *`}
                                value={beneficialOwner.socSecurityNumber}
                                onChange={(e) => {
                                  const newArray = [...beneficialOwnerPersons];
                                  newArray[index].socSecurityNumber =
                                    e.target.value;
                                  setBeneficialOwnerPersons(newArray);
                                  validateSSN(
                                    e.target.value,
                                    `${beneficialOwner.id}-ssn`
                                  );
                                }}
                                infoText={getErrorById(
                                  `${beneficialOwner.id}-ssn`
                                )}
                                maxLength={20}
                              />
                            </div>
                            <button
                              type="button"
                              className="modify-button remove-button"
                              onClick={() => {
                                deleteErrorsIncluding(beneficialOwner.id);
                                setBeneficialOwnerPersons(
                                  beneficialOwnerPersons.filter(
                                    (filterBeneficialOwner) => {
                                      return (
                                        filterBeneficialOwner !==
                                        beneficialOwner
                                      );
                                    }
                                  )
                                );
                              }}
                            >
                              <FontAwesomeIcon
                                className="icon"
                                icon={faMinus}
                              />
                              <p>{gTcf(t, '.btn.remove')}</p>
                            </button>
                          </div>
                          <div className="list-row">
                            <div className="tight-form-row">
                              <div className="tight-form-row">
                                <FormInput
                                  label={`${gTfil(
                                    t,
                                    '.share-in-percentage'
                                  )} *`}
                                  value={beneficialOwner.shares}
                                  regex={Regex.testIntegerInput}
                                  onChange={(e) => {
                                    const newArray = [
                                      ...beneficialOwnerPersons,
                                    ];
                                    newArray[index].shares = e.target.value;
                                    setBeneficialOwnerPersons(newArray);
                                    validateRequired(
                                      e.target.value,
                                      `${beneficialOwner.id}-shares`
                                    );
                                  }}
                                  infoText={getErrorById(
                                    `${beneficialOwner.id}-shares`
                                  )}
                                  maxLength={3}
                                />
                                <FormDropdown
                                  label={gTfdp(t, '.label-pep-rca')}
                                  onChange={(e) => {
                                    const newArray = [
                                      ...beneficialOwnerPersons,
                                    ];
                                    newArray[index].PEP =
                                      e.target.value === 'PEP';
                                    newArray[index].RCA =
                                      e.target.value === 'RCA';

                                    setBeneficialOwnerPersons(newArray);
                                  }}
                                  defaultValue="none"
                                >
                                  <option selected value="none">
                                    {gT(t, '.no')}
                                  </option>
                                  <option value="PEP">PEP</option>
                                  <option value="RCA">RCA</option>
                                </FormDropdown>
                              </div>
                              <div className="form-row" />
                              <div className="form-row" />
                            </div>
                          </div>
                        </div>
                      ))}
                      <button
                        className="modify-button"
                        type="button"
                        onClick={() => addBeneficialOwnerPersonEntry()}
                      >
                        <FontAwesomeIcon className="icon" icon={faPlus} />
                        <p>Lägg till person</p>
                      </button>
                    </div>
                    <div className="form-column">
                      <p className="small-title">
                        {gT(t, '.small-title.company')}
                      </p>
                      {/* eslint-disable-next-line prettier/prettier */}
                      {beneficialOwnerCompanies.map(
                        (beneficialOwner, index) => (
                          <div className="list-entry" key={beneficialOwner.id}>
                            <div className="list-row">
                              <div className="tight-form-row">
                                <FormInput
                                  label={`${gTfil(t, '.companys-name')} *`}
                                  value={beneficialOwner.name}
                                  onChange={(e) => {
                                    const newArray = [
                                      ...beneficialOwnerCompanies,
                                    ];
                                    newArray[index].name = e.target.value;
                                    setBeneficialOwnerCompanies(newArray);
                                    validateRequired(
                                      e.target.value,
                                      `${beneficialOwner.id}-name`
                                    );
                                  }}
                                  infoText={getErrorById(
                                    `${beneficialOwner.id}-name`
                                  )}
                                  maxLength={100}
                                />
                                <FormInput
                                  label={`${gTfil(t, '.orgnr')} *`}
                                  value={beneficialOwner.orgNumber}
                                  onChange={(e) => {
                                    const newArray = [
                                      ...beneficialOwnerCompanies,
                                    ];
                                    newArray[index].orgNumber = e.target.value;
                                    setBeneficialOwnerCompanies(newArray);
                                    validateOrgNr(
                                      e.target.value,
                                      `${beneficialOwner.id}-orgNumber`
                                    );
                                  }}
                                  infoText={getErrorById(
                                    `${beneficialOwner.id}-orgNumber`
                                  )}
                                  maxLength={20}
                                />
                                <div className="tight-form-row">
                                  <FormInput
                                    label={`${gTfil(
                                      t,
                                      '.share-in-percentage'
                                    )} *`}
                                    value={beneficialOwner.shares}
                                    onChange={(e) => {
                                      const newArray = [
                                        ...beneficialOwnerCompanies,
                                      ];
                                      newArray[index].shares = e.target.value;
                                      setBeneficialOwnerCompanies(newArray);
                                      validateRequired(
                                        e.target.value,
                                        `${beneficialOwner.id}-shares`
                                      );
                                    }}
                                    infoText={getErrorById(
                                      `${beneficialOwner.id}-shares`
                                    )}
                                    maxLength={3}
                                  />
                                  <div className="form-row" />
                                </div>
                              </div>
                              <button
                                type="button"
                                className="modify-button remove-button"
                                onClick={() => {
                                  deleteErrorsIncluding(beneficialOwner.id);
                                  setBeneficialOwnerCompanies(
                                    beneficialOwnerCompanies.filter(
                                      (filterBeneficialOwner) => {
                                        return (
                                          filterBeneficialOwner !==
                                          beneficialOwner
                                        );
                                      }
                                    )
                                  );
                                }}
                              >
                                <FontAwesomeIcon
                                  className="icon"
                                  icon={faMinus}
                                />
                                <p>{gTcf(t, '.btn.remove')}</p>
                              </button>
                            </div>
                          </div>
                        )
                      )}
                      <button
                        className="modify-button"
                        type="button"
                        onClick={addBeneficialOwnerCompanyEntry}
                      >
                        <FontAwesomeIcon className="icon" icon={faPlus} />
                        <p>{gTcf(t, '.btn.add-company')}</p>
                      </button>
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="form-column">
                      <FormTextArea
                        label={`${gTftal(
                          t,
                          '.company-hierarchy-description'
                        )} *`}
                        rows={6}
                        value={companyHierarchyDescription}
                        onChange={(e) => {
                          setCompanyHierarchyDescription(e.target.value);
                          validateRequired(
                            e.target.value,
                            'companyHierarchyDescription'
                          );
                        }}
                        infoText={getErrorById('companyHierarchyDescription')}
                        maxLength={1000}
                      />
                      <FormFileUpload
                        label={gTfful(t, '.company-hierarchy-file')}
                        onChange={(e) => {
                          if (e.target.files.length > 0) {
                            setCompanyHierarchyFile(e.target.files[0]);
                          }
                        }}
                        value={companyHierarchyFile}
                        t={t}
                      />
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="radio-select">
                      <p className="small-title">
                        {gT(t, '.small-title.other-influential-owners-present')}
                      </p>
                      <div className="choice-row">
                        <RadioButton
                          text={gT(t, '.yes')}
                          active={otherInfluentialOwnersPresent}
                          onClick={() => setOtherInfluentialOwnersPresent(true)}
                        />
                        <RadioButton
                          text={gT(t, '.no')}
                          active={!otherInfluentialOwnersPresent}
                          onClick={() =>
                            setOtherInfluentialOwnersPresent(false)
                          }
                        />
                      </div>
                    </div>
                    {otherInfluentialOwnersPresent && (
                      <>
                        <div className="form-column">
                          <p className="small-title">
                            {gT(t, '.small-title.people')}
                          </p>
                          <div className="form-column">
                            {/* eslint-disable-next-line */}
                            {otherInfluentialOwnerPersons.map(
                              (influentialPerson, index) => (
                                <div
                                  key={influentialPerson.id}
                                  className="list-entry"
                                >
                                  <div className="list-row">
                                    <div className="tight-form-row">
                                      <FormInput
                                        label={`${gTfil(t, '.first-name')} *`}
                                        value={influentialPerson.firstName}
                                        onChange={(e) => {
                                          const newArray = [
                                            ...otherInfluentialOwnerPersons,
                                          ];
                                          newArray[index].firstName =
                                            e.target.value;
                                          setOtherInfluentialOwnerPersons(
                                            newArray
                                          );
                                          validateRequired(
                                            e.target.value,
                                            `${influentialPerson.id}-first-name`
                                          );
                                        }}
                                        infoText={getErrorById(
                                          `${influentialPerson.id}-first-name`
                                        )}
                                        maxLength={100}
                                      />
                                      <FormInput
                                        label={`${gTfil(t, '.last-name')} *`}
                                        value={influentialPerson.lastName}
                                        onChange={(e) => {
                                          const newArray = [
                                            ...otherInfluentialOwnerPersons,
                                          ];
                                          newArray[index].lastName =
                                            e.target.value;
                                          setOtherInfluentialOwnerPersons(
                                            newArray
                                          );
                                          validateRequired(
                                            e.target.value,
                                            `${influentialPerson.id}-last-name`
                                          );
                                        }}
                                        infoText={getErrorById(
                                          `${influentialPerson.id}-last-name`
                                        )}
                                        maxLength={100}
                                      />
                                      <FormInput
                                        label={`${gTfil(
                                          t,
                                          '.soc-security-number'
                                        )} *`}
                                        value={
                                          influentialPerson.socSecurityNumber
                                        }
                                        onChange={(e) => {
                                          const newArray = [
                                            ...otherInfluentialOwnerPersons,
                                          ];
                                          newArray[index].socSecurityNumber =
                                            e.target.value;
                                          setOtherInfluentialOwnerPersons(
                                            newArray
                                          );
                                          validateSSN(
                                            e.target.value,
                                            `${influentialPerson.id}-ssn`
                                          );
                                        }}
                                        infoText={getErrorById(
                                          `${influentialPerson.id}-ssn`
                                        )}
                                        maxLength={20}
                                      />
                                      <FormDropdown
                                        label={gTfdp(t, '.label-pep-rca')}
                                        onChange={(e) => {
                                          const newArray = [
                                            ...otherInfluentialOwnerPersons,
                                          ];
                                          newArray[index].PEP =
                                            e.target.value === 'PEP';
                                          newArray[index].RCA =
                                            e.target.value === 'RCA';

                                          setOtherInfluentialOwnerPersons(
                                            newArray
                                          );
                                        }}
                                        defaultValue="none"
                                      >
                                        <option value="none">
                                          {gT(t, '.no')}
                                        </option>
                                        <option value="PEP">PEP</option>
                                        <option value="RCA">RCA</option>
                                      </FormDropdown>
                                    </div>
                                    <button
                                      type="button"
                                      className="modify-button remove-button"
                                      onClick={() => {
                                        deleteErrorsIncluding(
                                          influentialPerson.id
                                        );
                                        setOtherInfluentialOwnerPersons(
                                          otherInfluentialOwnerPersons.filter(
                                            (filterInfluential) => {
                                              return (
                                                filterInfluential !==
                                                influentialPerson
                                              );
                                            }
                                          )
                                        );
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        className="icon"
                                        icon={faMinus}
                                      />
                                      <p>{gTcf(t, '.btn.remove')}</p>
                                    </button>
                                  </div>
                                  <div className="list-row">
                                    <FormTextArea
                                      label={`${gTftal(
                                        t,
                                        '.description-of-influence-in-company'
                                      )} *`}
                                      rows={2}
                                      value={
                                        influentialPerson.descriptionOfInfluenceInCompany
                                      }
                                      onChange={(e) => {
                                        const newArray = [
                                          ...otherInfluentialOwnerPersons,
                                        ];
                                        newArray[
                                          index
                                        ].descriptionOfInfluenceInCompany =
                                          e.target.value;
                                        setOtherInfluentialOwnerPersons(
                                          newArray
                                        );
                                        validateRequired(
                                          e.target.value,
                                          `${influentialPerson.id}-description`
                                        );
                                      }}
                                      infoText={getErrorById(
                                        `${influentialPerson.id}-description`
                                      )}
                                      maxLength={1000}
                                    />
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                          <button
                            className="modify-button"
                            type="button"
                            onClick={() => addOtherInfluentialPerson()}
                          >
                            <FontAwesomeIcon className="icon" icon={faPlus} />
                            <p>{gTcf(t, '.btn.add-person')}</p>
                          </button>
                        </div>
                        <div className="form-column">
                          <p className="small-title">
                            {gT(t, '.small-title.company')}
                          </p>
                          <div className="form-column">
                            {/* eslint-disable-next-line */}
                            {otherInfluentialOwnerCompanies.map(
                              (influentialCompany, index) => (
                                <div
                                  className="list-entry"
                                  key={influentialCompany.id}
                                >
                                  <div className="list-row">
                                    <div className="tight-form-row">
                                      <FormInput
                                        label={`${gTfil(
                                          t,
                                          '.companys-name'
                                        )} *`}
                                        value={influentialCompany.name}
                                        onChange={(e) => {
                                          const newArray = [
                                            ...otherInfluentialOwnerCompanies,
                                          ];
                                          newArray[index].name = e.target.value;
                                          setOtherInfluentialOwnerCompanies(
                                            newArray
                                          );
                                          validateRequired(
                                            e.target.value,
                                            `${influentialCompany.id}-name`
                                          );
                                        }}
                                        infoText={getErrorById(
                                          `${influentialCompany.id}-name`
                                        )}
                                        maxLength={100}
                                      />
                                      <FormInput
                                        label={`${gTfil(t, '.orgnr')} *`}
                                        value={influentialCompany.orgNumber}
                                        onChange={(e) => {
                                          const newArray = [
                                            ...otherInfluentialOwnerCompanies,
                                          ];
                                          newArray[index].orgNumber =
                                            e.target.value;
                                          setOtherInfluentialOwnerCompanies(
                                            newArray
                                          );
                                          validateOrgNr(
                                            e.target.value,
                                            `${influentialCompany.id}-orgNumber`
                                          );
                                        }}
                                        infoText={getErrorById(
                                          `${influentialCompany.id}-orgNumber`
                                        )}
                                        maxLength={20}
                                      />
                                    </div>
                                    <button
                                      type="button"
                                      className="modify-button remove-button"
                                      onClick={() => {
                                        deleteErrorById(
                                          `${influentialCompany.id}-name`
                                        );
                                        deleteErrorById(
                                          `${influentialCompany.id}-orgNumber`
                                        );
                                        setOtherInfluentialOwnerCompanies(
                                          otherInfluentialOwnerCompanies.filter(
                                            (filterInfluential) => {
                                              return (
                                                filterInfluential !==
                                                influentialCompany
                                              );
                                            }
                                          )
                                        );
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        className="icon"
                                        icon={faMinus}
                                      />
                                      <p>{gTcf(t, '.btn.remove')}</p>
                                    </button>
                                  </div>
                                  <div className="list-row">
                                    <FormTextArea
                                      rows={2}
                                      label={`${gTftal(
                                        t,
                                        '.description-of-influence-in-company'
                                      )} *`}
                                      value={
                                        influentialCompany.descriptionOfInfluenceInCompany
                                      }
                                      onChange={(e) => {
                                        const newArray = [
                                          ...otherInfluentialOwnerCompanies,
                                        ];
                                        newArray[
                                          index
                                        ].descriptionOfInfluenceInCompany =
                                          e.target.value;
                                        setOtherInfluentialOwnerCompanies(
                                          newArray
                                        );
                                        validateRequired(
                                          e.target.value,
                                          `${influentialCompany.id}-description`
                                        );
                                      }}
                                      infoText={getErrorById(
                                        `${influentialCompany.id}-description`
                                      )}
                                      maxLength={1000}
                                    />
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                          <button
                            className="modify-button"
                            type="button"
                            onClick={() => addOtherInfluentialCompany()}
                          >
                            <FontAwesomeIcon className="icon" icon={faPlus} />
                            <p>{gTcf(t, '.btn.add-company')}</p>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="form-section">
                    <div className="form-column">
                      <p className="medium-title">
                        {gT(t, '.medium-title.business')}
                      </p>
                      <FormTextArea
                        label={`${gTftal(t, '.core-business-description')} *`}
                        rows={6}
                        value={coreBusinessDescription}
                        onChange={(e) => {
                          setCoreBusinessDescription(e.target.value);
                          validateRequired(
                            e.target.value,
                            'coreBusinessDescription'
                          );
                        }}
                        infoText={getErrorById('coreBusinessDescription')}
                        maxLength={2000}
                      />

                      <FormTextArea
                        label={`${gTftal(
                          t,
                          '.detailed-business-description'
                        )} *`}
                        rows={6}
                        value={detailedBusinessDescription}
                        onChange={(e) => {
                          setDetailedBusinessDescription(e.target.value);
                          validateRequired(
                            e.target.value,
                            'detailedBusinessDescription'
                          );
                        }}
                        infoText={getErrorById('detailedBusinessDescription')}
                        maxLength={2000}
                      />
                      <FormTextArea
                        label={`${gTftal(
                          t,
                          '.business-finance-description'
                        )} *`}
                        rows={6}
                        value={businessFinanceDescription}
                        onChange={(e) => {
                          setBusinessFinanceDescription(e.target.value);
                          validateRequired(
                            e.target.value,
                            'businessFinanceDescription'
                          );
                        }}
                        infoText={getErrorById('businessFinanceDescription')}
                        maxLength={2000}
                      />
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="radio-select">
                      <p className="small-title">
                        {gT(t, '.small-title.does-business-abroad')}
                      </p>
                      <div className="choice-row">
                        <RadioButton
                          text={gT(t, '.yes')}
                          active={doesBusinessAbroad}
                          onClick={() => setDoesBusinessAbroad(true)}
                        />
                        <RadioButton
                          text={gT(t, '.no')}
                          active={!doesBusinessAbroad}
                          onClick={() => setDoesBusinessAbroad(false)}
                        />
                      </div>
                    </div>
                    {doesBusinessAbroad && (
                      <>
                        <FormTextArea
                          label={`${gTftal(
                            t,
                            '.business-abroad-description'
                          )} *`}
                          value={businessAbroadDescription}
                          onChange={(e) => {
                            setBusinessAbroadDescription(e.target.value);
                            validateRequired(
                              e.target.value,
                              'businessAbroadDescription'
                            );
                          }}
                          rows={6}
                          infoText={getErrorById('businessAbroadDescription')}
                          maxLength={2000}
                        />
                        <div className="form-row">
                          <div className="radio-select">
                            <p className="small-title">
                              {gT(t, '.small-title.foreign-transaction-amount')}
                            </p>
                            <div className="choice-column">
                              <RadioButton
                                text="0 - 100 000"
                                active={foreignTransactionAmount === 10}
                                onClick={() => setForeignTransactionAmount(10)}
                              />
                              <RadioButton
                                text="100 000 - 500 000"
                                active={foreignTransactionAmount === 20}
                                onClick={() => setForeignTransactionAmount(20)}
                              />
                              <RadioButton
                                text="500 000-1 000 000"
                                active={foreignTransactionAmount === 30}
                                onClick={() => setForeignTransactionAmount(30)}
                              />
                              <RadioButton
                                text={`${gT(t, '.prefix-above')} 1 000 000`}
                                active={foreignTransactionAmount === 40}
                                onClick={() => setForeignTransactionAmount(40)}
                              />
                            </div>
                          </div>
                          <div className="radio-select">
                            <p className="small-title">
                              {gT(t, '.small-title.nr-of-foreign-transactions')}
                            </p>
                            <div className="choice-column">
                              <RadioButton
                                text="0"
                                active={nrOfForeignTransactions === 0}
                                onClick={() => setNrOfForeignTransactions(0)}
                              />
                              <RadioButton
                                text="1 - 2"
                                active={nrOfForeignTransactions === 1}
                                onClick={() => setNrOfForeignTransactions(1)}
                              />
                              <RadioButton
                                text="3 - 5"
                                active={nrOfForeignTransactions === 2}
                                onClick={() => setNrOfForeignTransactions(2)}
                              />
                              <RadioButton
                                text="6 - 8"
                                active={nrOfForeignTransactions === 3}
                                onClick={() => setNrOfForeignTransactions(3)}
                              />
                              <RadioButton
                                text="9 - 15"
                                active={nrOfForeignTransactions === 4}
                                onClick={() => setNrOfForeignTransactions(4)}
                              />
                              <RadioButton
                                text={`${gT(t, '.prefix-more-than')} 15`}
                                active={nrOfForeignTransactions === 5}
                                onClick={() => setNrOfForeignTransactions(5)}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="form-section">
                    <div className="radio-select">
                      <p className="small-title">
                        {gT(t, '.small-title.operations-outside-euees')}
                      </p>
                      <div className="choice-row">
                        <RadioButton
                          text={gT(t, '.yes')}
                          active={operationsOutsideEUEES}
                          onClick={() => setOperationsOutsideEUESS(true)}
                        />
                        <RadioButton
                          text={gT(t, '.no')}
                          active={!operationsOutsideEUEES}
                          onClick={() => setOperationsOutsideEUESS(false)}
                        />
                      </div>
                    </div>
                    {operationsOutsideEUEES && (
                      <FormTextArea
                        label={`${gTftal(
                          t,
                          '.operations-uutside-euees-description'
                        )} *`}
                        rows={6}
                        value={operationsOutsideEUEESDescription}
                        onChange={(e) => {
                          setOperationsOutsideEUESSDescription(e.target.value);
                          validateRequired(
                            e.target.value,
                            'operationsOutsideEUEESDescription'
                          );
                        }}
                        infoText={getErrorById(
                          'operationsOutsideEUEESDescription'
                        )}
                        maxLength={2000}
                      />
                    )}
                  </div>
                </ClassicForm>
              ) : formStep === 3 ? (
                <ClassicForm>
                  <div className="form-section">
                    <div className="form-column">
                      <p className="medium-title">
                        {gT(t, '.medium-title.loan-purpose')}
                      </p>
                      <FormTextArea
                        label={`${gTftal(t, '.loan-purpose')} *`}
                        rows={6}
                        value={loanPurpose}
                        onChange={(e) => {
                          setLoanPurpose(e.target.value);
                          validateRequired(e.target.value, 'loanPurpose');
                        }}
                        infoText={getErrorById('loanPurpose')}
                        maxLength={1000}
                      />
                      {formType === '0' && (
                        <FormTextArea
                          rows={6}
                          label={`${gTftal(t, '.detailed-loan-purpose')} *`}
                          value={detailedLoanPurpose}
                          onChange={(e) => {
                            setDetailedLoanPurpose(e.target.value);
                            validateRequired(
                              e.target.value,
                              'detailedLoanPurpose'
                            );
                          }}
                          infoText={getErrorById('detailedLoanPurpose')}
                          maxLength={2000}
                        />
                      )}
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="form-column">
                      <p className="medium-title">
                        {gT(t, '.medium-title.future-plans')}
                      </p>
                      <FormTextArea
                        rows={6}
                        label={`${gTftal(t, '.future-plans')} *`}
                        value={futurePlans}
                        onChange={(e) => {
                          setFuturePlans(e.target.value);
                          validateRequired(e.target.value, 'futurePlans');
                        }}
                        infoText={getErrorById('futurePlans')}
                        maxLength={2000}
                      />
                      <div className="form-row">
                        <div className="form-column">
                          <FormInput
                            label={`${gTfil(
                              t,
                              '.estimated-revenue-three-months'
                            )} *`}
                            value={estimatedRevenueThreeMonths}
                            regex={Regex.testIntegerInput}
                            onChange={(e) => {
                              setEstimatedRevenueThreeMonths(e.target.value);
                              validateRequired(
                                e.target.value,
                                'estimatedRevenueThreeMonths'
                              );
                            }}
                            infoText={getErrorById(
                              'estimatedRevenueThreeMonths'
                            )}
                            maxLength={100}
                          />
                          <FormInput
                            label={`${gTfil(
                              t,
                              '.estimated-revenue-six-months'
                            )} *`}
                            value={estimatedRevenueSixMonths}
                            regex={Regex.testIntegerInput}
                            onChange={(e) => {
                              setEstimatedRevenueSixMonths(e.target.value);
                              validateRequired(
                                e.target.value,
                                'estimatedRevenueSixMonths'
                              );
                            }}
                            infoText={getErrorById('estimatedRevenueSixMonths')}
                            maxLength={100}
                          />
                          <FormInput
                            label={`${gTfil(
                              t,
                              '.estimated-revenue-twelve-months'
                            )} *`}
                            value={estimatedRevenueTwelveMonths}
                            regex={Regex.testIntegerInput}
                            onChange={(e) => {
                              setEstimatedRevenueTwelveMonths(e.target.value);
                              validateRequired(
                                e.target.value,
                                'estimatedRevenueTwelveMonths'
                              );
                            }}
                            infoText={getErrorById(
                              'estimatedRevenueTwelveMonths'
                            )}
                            maxLength={100}
                          />
                        </div>
                        <div className="form-column">
                          <FormInput
                            label={`${gTfil(
                              t,
                              '.estimated-result-three-months'
                            )} *`}
                            value={estimatedResultThreeMonths}
                            regex={Regex.testIntegerInput}
                            onChange={(e) => {
                              setEstimatedResultThreeMonths(e.target.value);
                              validateRequired(
                                e.target.value,
                                'estimatedResultThreeMonths'
                              );
                            }}
                            infoText={getErrorById(
                              'estimatedResultThreeMonths'
                            )}
                            maxLength={100}
                          />
                          <FormInput
                            label={`${gTfil(
                              t,
                              '.estimated-result-six-months'
                            )} *`}
                            value={estimatedResultSixMonths}
                            regex={Regex.testIntegerInput}
                            onChange={(e) => {
                              setEstimatedResultSixMonths(e.target.value);
                              validateRequired(
                                e.target.value,
                                'estimatedResultSixMonths'
                              );
                            }}
                            infoText={getErrorById('estimatedResultSixMonths')}
                            maxLength={100}
                          />
                          <FormInput
                            label={`${gTfil(
                              t,
                              '.estimated-result-twelve-months'
                            )} *`}
                            value={estimatedResultTwelveMonths}
                            regex={Regex.testIntegerInput}
                            onChange={(e) => {
                              setEstimatedResultTwelveMonths(e.target.value);
                              validateRequired(
                                e.target.value,
                                'estimatedResultTwelveMonths'
                              );
                            }}
                            infoText={getErrorById(
                              'estimatedResultTwelveMonths'
                            )}
                            maxLength={100}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="radio-select">
                      <p className="medium-title">
                        {gT(t, '.medium-title.revenue-monthly')}
                      </p>
                      <FormInput
                        label={`${gTfil(t, '.revenue-monthly')} *`}
                        value={revenueMonthly}
                        regex={Regex.testIntegerInput}
                        onChange={(e) => {
                          setRevenueMothly(e.target.value);
                          validateRequired(e.target.value, 'revenueMonthly');
                        }}
                        infoText={getErrorById('revenueMonthly')}
                        maxLength={100}
                      />
                      <p className="normal-text">
                        {gT(t, '.normal-text.specify-monthly-revenue')}
                      </p>
                      <div className="choice-row">
                        <RadioButton
                          text={gTrb(t, '.selling-product')}
                          active={sellingProduct}
                          onClick={() => setSellingProduct(!sellingProduct)}
                          checkBox
                        />
                        <RadioButton
                          text={gTrb(t, '.selling-Service')}
                          active={sellingService}
                          onClick={() => setSellingService(!sellingService)}
                          checkBox
                        />
                        <RadioButton
                          text={gTrb(t, '.other-revenue-source')}
                          active={otherRevenueSource}
                          onClick={() =>
                            setOtherRevenueSource(!otherRevenueSource)
                          }
                          checkBox
                        />
                      </div>
                    </div>
                    {sellingProduct && (
                      <FormTextArea
                        rows={4}
                        label={`${gTftal(t, '.product-description')} *`}
                        value={productDescription}
                        onChange={(e) => {
                          setProductDescription(e.target.value);
                          validateRequired(
                            e.target.value,
                            'productDescription'
                          );
                        }}
                        infoText={getErrorById('productDescription')}
                        maxLength={2000}
                      />
                    )}
                    {sellingService && (
                      <FormTextArea
                        rows={4}
                        label={`${gTftal(t, '.service-description')} *`}
                        value={serviceDescription}
                        onChange={(e) => {
                          setServiceDescription(e.target.value);
                          validateRequired(
                            e.target.value,
                            'serviceDescription'
                          );
                        }}
                        infoText={getErrorById('serviceDescription')}
                        maxLength={2000}
                      />
                    )}
                    {otherRevenueSource && (
                      <FormTextArea
                        rows={4}
                        label={`${gTftal(
                          t,
                          '.other-revenue-source-description'
                        )} *`}
                        value={otherRevenueSourceDescription}
                        onChange={(e) => {
                          setOtherRevenueSourceDescription(e.target.value);
                          validateRequired(
                            e.target.value,
                            'otherRevenueSourceDescription'
                          );
                        }}
                        infoText={getErrorById('otherRevenueSourceDescription')}
                        maxLength={2000}
                      />
                    )}
                  </div>
                  {formType === '2' && (
                    <div className="form-section">
                      <div className="form-grid">
                        <FormInput
                          label={`${gTfil(
                            t,
                            '.estimated-monthly-revenue-institute'
                          )} *`}
                          value={estimatedMonthlyRevenueInstitute}
                          regex={Regex.testIntegerInput}
                          onChange={(e) => {
                            setEstimatedMonthlyRevenueInstitute(e.target.value);
                            validateRequired(
                              e.target.value,
                              'estimatedMonthlyRevenueInstitute'
                            );
                          }}
                          infoText={getErrorById(
                            'estimatedMonthlyRevenueInstitute'
                          )}
                          maxLength={100}
                        />
                        <FormInput
                          label={`${gTfil(
                            t,
                            '.nr-of-transactions-institute'
                          )} *`}
                          value={nrOfTransactionsInstitute}
                          regex={Regex.testIntegerInput}
                          onChange={(e) => {
                            setNrOfTransactionsInstitute(e.target.value);
                            validateRequired(
                              e.target.value,
                              'nrOfTransactionsInstitute'
                            );
                          }}
                          infoText={getErrorById('nrOfTransactionsInstitute')}
                          maxLength={100}
                        />
                        <FormInput
                          label={`${gTfil(t, '.average-amount-invoice')} *`}
                          value={averageAmountInvoice}
                          regex={Regex.testIntegerInput}
                          onChange={(e) => {
                            setAverageAmountInvoice(e.target.value);
                            validateRequired(
                              e.target.value,
                              'averageAmountInvoice'
                            );
                          }}
                          infoText={getErrorById('averageAmountInvoice')}
                          maxLength={100}
                        />
                      </div>
                    </div>
                  )}
                </ClassicForm>
              ) : formStep === 4 ? (
                <ClassicForm>
                  <div className="form-section">
                    <div className="radio-select">
                      <p className="small-title">
                        {gT(t, '.small-title.currency-exchange')}
                      </p>
                      <div className="choice-row">
                        <RadioButton
                          text={gT(t, '.yes')}
                          active={currencyExchange}
                          onClick={() => setCurrencyExchange(true)}
                        />
                        <RadioButton
                          text={gT(t, '.no')}
                          active={!currencyExchange}
                          onClick={() => setCurrencyExchange(false)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="radio-select">
                      <p className="small-title">
                        {gT(t, '.small-title.crypto-currency-exchange')}
                      </p>
                      <div className="choice-row">
                        <RadioButton
                          text={gT(t, '.yes')}
                          active={cryptoCurrencyExchange}
                          onClick={() => setCryptoCurrencyExchange(true)}
                        />
                        <RadioButton
                          text={gT(t, '.no')}
                          active={!cryptoCurrencyExchange}
                          onClick={() => setCryptoCurrencyExchange(false)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="radio-select">
                      <p className="small-title">
                        {gT(t, '.small-title.handling-cash')}
                      </p>
                      <div className="choice-row">
                        <RadioButton
                          text={gT(t, '.yes')}
                          active={handlingCash}
                          onClick={() => setHandlingCash(true)}
                        />
                        <RadioButton
                          text={gT(t, '.no')}
                          active={!handlingCash}
                          onClick={() => setHandlingCash(false)}
                        />
                      </div>
                    </div>
                    {/* handlingCash && (
                      <FormInput
                        label="Ange belopp i kassa *"
                        value={cashMonthly}
                        regex={Regex.testIntegerInput}
                        onChange={(e) => {
                          setCashMonthly(e.target.value);
                          validateRequired(e.target.value, 'cashMonthly');
                        }}
                        infoText={getErrorById('cashMonthly')}
                        maxLength={100}
                      />
                    ) */}
                  </div>
                  <div className="form-section">
                    <div className="radio-select">
                      <p className="small-title">
                        {gT(t, '.small-title.deposits')}
                      </p>
                      <p className="normal-text">
                        {gT(t, '.normal-text.deposits')}
                      </p>
                      <div className="choice-column">
                        <RadioButton
                          text={gT(t, '.no')}
                          active={nrOfExtraRepayments === 0}
                          onClick={() => setNrOfExtraRepayments(0)}
                        />
                        <RadioButton
                          text="1 - 2"
                          active={nrOfExtraRepayments === 1}
                          onClick={() => setNrOfExtraRepayments(1)}
                        />
                        <RadioButton
                          text="2 - 4"
                          active={nrOfExtraRepayments === 2}
                          onClick={() => setNrOfExtraRepayments(2)}
                        />
                        <RadioButton
                          text="5 - 8"
                          active={nrOfExtraRepayments === 3}
                          onClick={() => setNrOfExtraRepayments(3)}
                        />
                        <RadioButton
                          text={`${gT(t, '.prefix-more-than')} 8`}
                          active={nrOfExtraRepayments === 4}
                          onClick={() => setNrOfExtraRepayments(4)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="form-column">
                      <p className="small-title">
                        {gT(t, '.small-title.withdrawals')}
                      </p>
                      <p className="normal-text">
                        {gT(t, '.normal-text.withdrawals')}
                      </p>
                      <FormInput
                        label={`${gTfil(t, '.nr-of-withdrawals')} *`}
                        value={nrOfWithdrawals}
                        regex={Regex.testIntegerInput}
                        onChange={(e) => {
                          setNrOfWithdrawals(e.target.value);
                          validateRequired(e.target.value, 'nrOfWithdrawals');
                        }}
                        infoText={getErrorById('nrOfWithdrawals')}
                        maxLength={100}
                      />
                    </div>
                  </div>
                </ClassicForm>
              ) : formStep === 5 ? (
                <ClassicForm>
                  <div className="form-section">
                    <div className="form-column">
                      <p className="medium-title">
                        {gT(t, '.medium-title.submit')}
                      </p>
                      <p className="small-title">
                        {gT(t, '.small-title.signer')}
                      </p>
                      <div className="form-column">
                        {possibleSubjects.map((subject, index) => (
                          <div className="subject-row" key={subject.id}>
                            <RadioButton
                              text={`${subject.firstName} ${subject.lastName}`}
                              checkBox
                              active={subject.selected}
                              onClick={() => {
                                const newArray = [...possibleSubjects];
                                newArray[index].selected = !subject.selected;
                                setPossibleSubjects(newArray);
                              }}
                            />
                            {subject.selected && (
                              <>
                                <FormInput
                                  label={`${gTfil(t, '.email')} *`}
                                  value={subject.email}
                                  onChange={(e) => {
                                    const newArray = [...possibleSubjects];
                                    newArray[index].email = e.target.value;
                                    setPossibleSubjects(newArray);
                                    validateEmail(
                                      e.target.value,
                                      `${subject.id}-email`
                                    );
                                  }}
                                  infoText={getErrorById(`${subject.id}-email`)}
                                  maxLength={100}
                                />
                                <FormFileUpload
                                  label={`${gTfful(t, '.id-file')} *`}
                                  onChange={(e) => {
                                    const newArray = [...possibleSubjects];
                                    if (e.target.files.length > 0) {
                                      const file = e.target.files[0];
                                      newArray[index].idFile = file;
                                      deleteErrorById(`${subject.id}-file`);
                                    } else {
                                      newArray[index].idFile = null;
                                      setErrorById(
                                        `${subject.id}-file`,
                                        fileRequiredErrorText
                                      );
                                    }
                                    setPossibleSubjects(newArray);
                                  }}
                                  infoText={getErrorById(`${subject.id}-file`)}
                                  value={subject.idFile}
                                  t={t}
                                />
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </ClassicForm>
              ) : (
                formStep === maxSteps + 1 && (
                  <div className="form-submitted-content">
                    <FontAwesomeIcon className="icon" icon={faCheckCircle} />
                    <p className="medium-title">
                      {gT(t, '.medium-title.thank-you')}&#33;
                    </p>
                    <p className="normal-text">
                      {gT(t, '.normal-text.submit-and-close')}
                    </p>
                  </div>
                )
              )}
            </div>
            {formStep !== maxSteps + 1 && (
              <div className="nav-footer">
                <div className="info-row">
                  {submissionError && (
                    <p className="submission-error normal-text">
                      {submissionError}
                    </p>
                  )}
                </div>
                <div className="button-row">
                  {formStep !== 1 && (
                    <XpektorButton
                      content={gT(t, '.xpektor-btn.previous-step')}
                      onClick={() => goPrevious()}
                    />
                  )}
                  <div />
                  <XpektorButton
                    content={
                      formStep < maxSteps
                        ? gT(t, '.xpektor-btn.next-step')
                        : gT(t, '.xpektor-btn.submit')
                    }
                    onClick={() => goNext()}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
